import { tenant } from '@tenant';
import type { Validate } from 'react-hook-form';

import { capitalizeFirstLetter } from 'utils/capitalize-first-letter';

import type { FieldObject, FieldObjectRules } from 'api';
import { IBANValidation } from 'validators/iban';

import type { DraftSteps } from './types';

/**
 * Simple mappings from name --> definition
 */
const BASE_FIELD_DEFINITION_MAP: BaseFieldDefinitionMap = {
  virtual_account_number: {
    label: 'Account',
    kind: 'virtual_account'
  },
  country_code: {
    label: 'Country',
    kind: 'country'
  },
  name: {
    label: 'Name',
    kind: 'text',
    placeholder: 'John Doe'
  },
  amount: {
    label: 'Amount',
    kind: 'amount'
  },
  website: {
    label: 'Website',
    kind: 'text',
    example: `E.g.: ${tenant.domains.root}`
  },
  iban: {
    label: 'IBAN',
    kind: 'iban',
    example: 'E.g.: GB29 NWBK 6016 1331 9268 19',
    validate: (iban: string) => (IBANValidation(iban) ? true : 'Invalid IBAN')
  },
  business_unit: {
    label: 'Business Unit',
    kind: 'business_unit'
  },
  end_to_end_id: {
    label: 'End-to-end ID',
    kind: 'text',
    tooltip:
      'Enter a unique ID (letters, numbers, - or _). It will follow the payment to the recipient and help you with reconciliation or tracking.'
  },
  bank_clearing_system_id: {
    label: 'Bank Clearing System ID',
    kind: 'text',
    tooltip:
      'The unique identifier for the local clearing system that processes this payment. Use the correct ID for the destination country.'
  },
  bank_clearing_system_code: {
    label: 'Bank Clearing System Code',
    kind: 'text',
    tooltip:
      'The standard code for the clearing system through which the payment will be routed. If the value is invalid, the correct code will be shown in the error message.'
  },
  e2e_test_case: {
    label: 'End-to-end sandbox test case',
    kind: 'e2e_test_case',
    optional: true
  }
};

/**
 * Specific field descriminated based on field --> definition
 */
const SPECIFIC_FIELD_DEFINITION_MAP: SpecificFieldDefinitionMap = {
  type: (field) =>
    field.step === 'creditor' || field.step === 'ultimate_debtor'
      ? {
          label: 'Type',
          kind: 'creditor_type'
        }
      : undefined,
  purpose_code: (field) =>
    field.rules?.external_code == 'B2B Purpose Codes'
      ? {
          label: 'Purpose Code',
          kind: 'b2b_purpose_code'
        }
      : {
          label: 'Purpose Code',
          kind: 'text',
          tooltip:
            "Enter the required code for this payment's purpose. If the value is invalid, the correct code will be shown in the error message.",
          placeholder: 'PC02'
        }
};

/**
 * Used to enhance the field definition with data such as:
 * input kind, label, placeholder text, etc.
 */
export const enhanceFieldDefinition = (
  rawStepField: RawStepField
): FieldDefinition => {
  const specific =
    rawStepField.name in SPECIFIC_FIELD_DEFINITION_MAP
      ? SPECIFIC_FIELD_DEFINITION_MAP[rawStepField.name](rawStepField)
      : undefined;

  const base =
    rawStepField.name in BASE_FIELD_DEFINITION_MAP
      ? BASE_FIELD_DEFINITION_MAP[rawStepField.name]
      : getBaseFieldDefinition(rawStepField);

  return {
    ...rawStepField,
    ...(specific ?? base)
  };
};

/**
 * Base definition
 */
const getBaseFieldDefinition = (
  field: RawStepField
): EnhancedFieldDefinition => {
  return {
    label: field.name.split('_').map(capitalizeFirstLetter).join(' '),
    kind: 'text'
  };
};

/**
 * Input types used by factory
 */
export type InputTypes =
  | 'text'
  | 'date'
  | 'virtual_account'
  | 'country'
  | 'iban'
  | 'amount'
  | 'business_unit'
  | 'b2b_purpose_code'
  | 'creditor_type'
  | 'e2e_test_case';

///////////
/**
 * Type definitions
 */
///////////

export type RawStepField = {
  definition: FieldObject;
  name: string;
  fullName: `${DraftSteps}.${string}`;
  step: DraftSteps;
  rules?: FieldObjectRules;
};

type EnhancedFieldDefinition = {
  label: string;
  kind: InputTypes;
  placeholder?: string;
  example?: string;
  tooltip?: string;
  optional?: boolean;
  validate?: Validate<string, unknown>;
};

export type FieldDefinition = RawStepField & EnhancedFieldDefinition;

type BaseFieldDefinitionMap = Record<string, EnhancedFieldDefinition>;

type SpecificFieldDefinitionMap = Record<
  string,
  (rawStepField: RawStepField) => EnhancedFieldDefinition | undefined
>;
